import GettingStarted from './components/GettingStarted/GettingStarted';
import PageNotFound from './components/PageNotFound';
import ApplicantProfileContainer from './containers/applicant_profile';
import ApplicationContainer from './containers/application';
import ApplicationDocumentationContainer from './containers/application_documentation';
import CandidateDashboardContainer from './containers/candidatedashboard';
import CandidatesListContainer from './containers/candidateslist';
import CurrentUserPermissionsProvider from './containers/CurrentUserPermissionsProvider';
import DashboardContainer from './containers/dashboard';
import PreferencesContainer from './containers/preferences';
import PipelineMetricsDashboard from './features/PipelineMetricsDashboard';
import EnterProspectForm from './features/Prospects/EnterProspectForm';
import Layout from './Layout';

import PublicRoutes from 'components/PublicRoutes';
import { NetworkRequestStatusProvider } from 'context/networkRequestStatusContext';
import FlatFileTransfer from 'features/FlatFileTransferDashboard';
import { Redirect, Route, Switch } from 'react-router-dom';
import ApplicationConfirmationComponent from './components/ApplicationConfirmation';
import AuthenticatedRoute from './components/AuthenticatedRoute';
import UserAuthorized from './components/UserAuthorized';
import ATSRoute from './containers/ATSRoute';
import Dashboards from './containers/Dashboards';
import District from './containers/District';
import DistrictUserJobCreateContainer from './containers/DistrictUserJobCreate';
import EditReferenceContainer from './containers/editreference';
import ProfileContainer from './containers/profile';
import School from './containers/School';
import ScorecardEditContainer from './containers/ScorecardEditContainer';
import TalentMarketplace from './containers/TalentMarketplace';
import { FeatureContextProvider } from './context/featureContext';
import { FlatFileTransferFields } from './features/FlatFileTransferDashboard/FlatFileTransferFields';
import {
  allAdminUserTypes,
  checklistUserTypes,
  dashboardsUserTypes,
  districtAdminUserTypes,
  hiringRequestsUserTypes,
  schoolOrDistrictAuthUserTypes,
  superAdminUserType,
} from './utils/userType';
import ScorecardPrintView from 'features/PrintView/ScorecardPrintView';
import { GlobalStyles, ThemeProvider } from '@mui/material';
import { nimbleTheme } from 'theme';
import { ReferencePrintView } from 'features/PrintView/ReferencePrintView';
import { StateParamProvider } from 'features/Connect/features/Context/ConnectStateCodeContextProvider';
import { SignupSourcesProvider } from 'context/signupSourcesContext';
import TrevorDashboard from './features/TrevorDashboard/TrevorDashboard';
import { NavigationProvider } from 'context/navigationContext';
import { HiringRequestsLandingPage } from 'features/Connect/features/ConnectATSIntegration/HiringRequestsLandingPage';
import { ConnectContextProvider } from 'features/Connect/features/Context/ConnectContextProvider';
import TabbedJobView from 'features/TabbedJobView/TabbedJobView';
import { ConfigProvider } from './context/ConfigContext';
import { ToastProvider } from './context/toastContext';
import { PrincipalCandidatesList } from 'features/Principal/PrincipalCandidatesList';
import { PrincipalHiringRequests } from 'features/Principal/PrincipalHiringRequests';
import { PrincipalInviteToApply } from 'features/Principal/PrincipalInviteToApply';

const App: React.FC = () => {
  return (
    <ConfigProvider>
      <FeatureContextProvider>
        <NavigationProvider>
          <ThemeProvider theme={nimbleTheme}>
            <GlobalStyles
              styles={{
                body: {
                  fontFamily: nimbleTheme.typography.fontFamily,
                },
              }}
            />
            <ToastProvider>
              <SignupSourcesProvider>
                <CurrentUserPermissionsProvider>
                  <StateParamProvider>
                    <ConnectContextProvider>
                      <Layout>
                        <Switch>
                          {/* / school routes */}
                          <AuthenticatedRoute path="/school">
                            <School />
                          </AuthenticatedRoute>

                          {/* /district routes */}
                          <AuthenticatedRoute path="/district">
                            <District />
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/data/onboarding-usage">
                            <UserAuthorized allow={districtAdminUserTypes}>
                              <NetworkRequestStatusProvider>
                                <TrevorDashboard />
                              </NetworkRequestStatusProvider>
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <Route path="/internal/jobview/:id" exact>
                            {({ match }) => (
                              <Redirect to={`/internal/jobview/${match.params.id}/details`} />
                            )}
                          </Route>
                          <AuthenticatedRoute path="/internal/jobview/:id/:tab">
                            <UserAuthorized allow={allAdminUserTypes}>
                              <TabbedJobView />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          {/* /dashboards routes */}
                          <AuthenticatedRoute path="/dashboards">
                            <Dashboards />
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/principal/candidates">
                            <UserAuthorized allow={['school_user']}>
                              <PrincipalCandidatesList />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/principal/hiring-requests">
                            <UserAuthorized allow={['school_user']}>
                              <PrincipalHiringRequests />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/principal/invite-to-apply">
                            <UserAuthorized allow={['school_user']}>
                              <PrincipalInviteToApply />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/applicant-profile/:id">
                            <UserAuthorized allow={['candidate']}>
                              <ApplicantProfileContainer />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/application/:id">
                            <UserAuthorized allow={['candidate']}>
                              <ApplicationContainer />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/application-documentation/:id">
                            <UserAuthorized allow={['candidate']}>
                              <ApplicationDocumentationContainer />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/candidate-dashboard">
                            <UserAuthorized allow={['candidate']}>
                              <CandidateDashboardContainer />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/applicationconfirmation">
                            <UserAuthorized allow={['candidate']}>
                              <ApplicationConfirmationComponent />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/preferences">
                            <UserAuthorized allow={['candidate']}>
                              <PreferencesContainer />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/teacherprofile">
                            <UserAuthorized allow={['candidate']}>
                              <ProfileContainer />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          <AuthenticatedRoute path="/getting-started">
                            <UserAuthorized allow={checklistUserTypes}>
                              <GettingStarted />
                            </UserAuthorized>
                          </AuthenticatedRoute>

                          {/* ATS Routes + dashboard auth */}
                          <ATSRoute path="/data/vacancy">
                            <UserAuthorized allow={dashboardsUserTypes}>
                              <DashboardContainer />
                            </UserAuthorized>
                          </ATSRoute>
                          <ATSRoute path="/data/pipeline" exact>
                            <UserAuthorized allow={dashboardsUserTypes}>
                              <PipelineMetricsDashboard />
                            </UserAuthorized>
                          </ATSRoute>

                          {/* requiresATS + superAdmin auth */}
                          <ATSRoute path="/editreference/:id">
                            <UserAuthorized allow={superAdminUserType}>
                              <EditReferenceContainer />
                            </UserAuthorized>
                          </ATSRoute>

                          <ATSRoute path="/createreference">
                            <UserAuthorized allow={superAdminUserType}>
                              <EditReferenceContainer />
                            </UserAuthorized>
                          </ATSRoute>

                          {/* requiresATS + district admin auth */}
                          <ATSRoute path="/prospectentry">
                            <UserAuthorized allow={districtAdminUserTypes}>
                              <EnterProspectForm />
                            </UserAuthorized>
                          </ATSRoute>

                          {/* requiresLogin + requiresATS + prospect auth */}
                          <ATSRoute path="/prospectlist">
                            <UserAuthorized allow={districtAdminUserTypes}>
                              <CandidatesListContainer />
                            </UserAuthorized>
                          </ATSRoute>

                          {/* requiresATS + school or district auth */}
                          <ATSRoute path="/editscorecard/:id">
                            <UserAuthorized allow={schoolOrDistrictAuthUserTypes}>
                              <ScorecardEditContainer />
                            </UserAuthorized>
                          </ATSRoute>

                          <ATSRoute path="/createscorecard">
                            <UserAuthorized allow={schoolOrDistrictAuthUserTypes}>
                              <ScorecardEditContainer />
                            </UserAuthorized>
                          </ATSRoute>

                          <ATSRoute path="/view-scorecard/:id">
                            <UserAuthorized allow={schoolOrDistrictAuthUserTypes}>
                              <ScorecardPrintView />
                            </UserAuthorized>
                          </ATSRoute>

                          <ATSRoute path="/data/datatransfer/fields/:id">
                            <UserAuthorized allow={schoolOrDistrictAuthUserTypes}>
                              <FlatFileTransferFields />
                            </UserAuthorized>
                          </ATSRoute>

                          <ATSRoute path="/data/datatransfer">
                            <UserAuthorized allow={schoolOrDistrictAuthUserTypes}>
                              <FlatFileTransfer />
                            </UserAuthorized>
                          </ATSRoute>

                          {/* super and district admin auth */}
                          <ATSRoute path="/talent-marketplace">
                            <UserAuthorized allow={districtAdminUserTypes}>
                              <TalentMarketplace />
                            </UserAuthorized>
                          </ATSRoute>

                          <ATSRoute path="/profile/:id/reference/:id">
                            <ReferencePrintView />
                          </ATSRoute>

                          <ATSRoute path="/hiring-requests/">
                            <UserAuthorized allow={hiringRequestsUserTypes}>
                              <HiringRequestsLandingPage />
                            </UserAuthorized>
                          </ATSRoute>

                          {/* Not working on prod */}
                          <AuthenticatedRoute
                            path="/districtuser/jobcreate"
                            component={DistrictUserJobCreateContainer}
                          />

                          {/* Dashboard Redirect */}
                          <Redirect from="/dashboard" to="/data/vacancy" />

                          {/* { public routes } */}
                          <Route path="*">
                            <PublicRoutes fallback={<PageNotFound />} />
                          </Route>
                        </Switch>
                      </Layout>
                    </ConnectContextProvider>
                  </StateParamProvider>
                </CurrentUserPermissionsProvider>
              </SignupSourcesProvider>
            </ToastProvider>
          </ThemeProvider>
        </NavigationProvider>
      </FeatureContextProvider>
    </ConfigProvider>
  );
};

export default App;
